import { ExclamationCircleIcon, PencilIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import RichTextEmbed from "src/components/embedTypes/basic/RichTextEmbed";
import FileUpload from "src/components/embedTypes/forms/FileUpload";
import FormChoiceGrid from "src/components/embedTypes/forms/FormChoiceGrid";
import Checkbox from "src/components/form/Checkbox";
import InputGroup from "src/components/form/Input";
import RadioGroup from "src/components/form/RadioGroup";
import SelectMenu from "src/components/form/SelectMenu";
import SignatureArea from "src/components/form/SignatureArea";
import TextArea from "src/components/form/TextArea";
import Modal from "src/components/shared/Modal";
import Flatpickr from "react-flatpickr";
import { classNames } from "./classNames";
import MultiSelectObject from "src/components/form/MultiSelectObject";
import { useState } from "react";

export const RenderQuestionField = ({ response = false, content = {}, answers = [], onHandleChange = () => {}, showSignatureModal = false, setShowSignatureModal = () => {}, dateOfSigning = "", showCustomTagModal, setShowCustomTagModal = () => {}, validationError = null }) => {
  const answer = answers?.find((ans) => ans?.content_id?.toString() === content?._id?.toString());
  const [customOptions, setCustomOptions] = useState(() => (Array.isArray(answer?.other_value) ? answer.other_value : []));
  const [realtimeError, setRealtimeError] = useState(null);

  const isRequired = content?.validation?.required;

  const questionLabel = (
    <label className="text-base font-semibold text-gray-700">
      {content?.name}
      {isRequired && <span className="pl-1 text-base font-semibold !leading-3 text-red-500">*</span>}
    </label>
  );

  const commitCustomOptions = () => {
    onHandleChange(content?._id, "other_value", customOptions, content?.question_type);
    setShowCustomTagModal(false);
  };

  const ErrorMessage = () => {
    const displayError = validationError || realtimeError;
    return displayError ? (
      <div className="mt-1 flex items-center gap-1 text-red-500">
        <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium">{displayError}</span>
      </div>
    ) : null;
  };

  const validateInputRealtime = (value) => {
    const validation = content?.validation;

    if (!value || !value.trim()) {
      setRealtimeError(null);
      return;
    }

    if (!validation?.custom_validation) {
      setRealtimeError(null);
      return;
    }

    const error = validateCustomInputField(value, validation, content);

    setRealtimeError(error);
  };

  switch (content?.question_type) {
    case "input":
      return (
        <div className="space-y-4">
          {questionLabel}
          {response ? (
            <p className="text-gray-500">{answer?.value || "No answer provided"}</p>
          ) : (
            <>
              <InputGroup
                inputPlaceholder="Your answer"
                error={!!validationError || !!realtimeError}
                inputValue={answer?.value || ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onHandleChange(content?._id, "value", newValue, content?.question_type);
                  validateInputRealtime(newValue);
                }}
              />
              <ErrorMessage />
            </>
          )}
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-4">
          {questionLabel}
          {response ? (
            <p className="text-gray-700">{answer?.value || "No answer provided"}</p>
          ) : (
            <>
              <TextArea
                placeholder="Your answer"
                value={answer?.value || ""}
                onChange={(e) => onHandleChange(content?._id, "value", e.target.value, content?.question_type)}
              />
              <ErrorMessage />
            </>
          )}
        </div>
      );

    case "rich_text":
      return (
        <div className="space-y-4">
          <label className="text-base font-semibold text-gray-700">{content?.name}</label>
          <RichTextEmbed content={{ rich_text: answer?.value }} />
        </div>
      );

    case "signature":
      return (
        <div className="flex flex-col space-y-4">
          {questionLabel}
          {response && !answer?.value ? (
            <p className="text-gray-700">{"Signature/initials not provided"}</p>
          ) : !answer?.value ? (
            <button
              className="inline-flex max-w-fit items-center justify-center gap-2 rounded-md border border-highlightColor px-4 py-3 font-medium text-highlightColor transition-colors hover:bg-highlightColor hover:text-white"
              onClick={() => setShowSignatureModal(true)}>
              <PencilIcon className="h-5 w-5" /> Add Signature
            </button>
          ) : (
            <div className="mt-2">
              <img
                onClick={() => {
                  if (!response) setShowSignatureModal(true);
                }}
                src={typeof answer.value === "string" ? answer.value : URL.createObjectURL(answer.value)}
                alt="Signature Preview"
                className={classNames("h-24 w-56  rounded-lg border transition-colors ", response ? "" : "cursor-pointer hover:border-highlightColor")}
              />
              {content?.show_date_on_signing && dateOfSigning && (
                <div className="text-right">
                  <span>{dateOfSigning}</span>
                </div>
              )}
            </div>
          )}
          {showSignatureModal && (
            <Modal
              isOpen={showSignatureModal}
              title="Add Your Signature"
              defaultOptions={{ onCancelButtonVisible: false, onSuccessButtonVisible: false }}
              onCancel={() => setShowSignatureModal(false)}>
              <SignatureArea
                value={answer?.value}
                alwaysShowButtons={true}
                onChange={(e) => {
                  onHandleChange(content?._id, "value", e, content?.question_type);
                  setShowSignatureModal(false);
                }}
              />
            </Modal>
          )}
        </div>
      );

    case "radio":
      return (
        <div className="space-y-4">
          <RadioGroup
            required={isRequired}
            name={`option-${content?._id}`}
            labelClass="!font-semibold"
            subtitle=""
            title={content?.name}
            defaultChecked={answer?.value}
            options={content?.options?.map((option) => ({
              id: option?._id,
              title: !option?.other ? option?.name : "Other",
            }))}
            onChange={(id) => onHandleChange(content?._id, "value", id, content?.question_type)}
          />
          {answer?.value === content?.options?.find((option) => option?.other)?._id && (
            <InputGroup
              error={isRequired}
              isDisabled={response}
              label="Other"
              inputValue={answer?.other_value || ""}
              onChange={(e) => onHandleChange(content?._id, "other_value", e.target.value, content?.question_type)}
            />
          )}
        </div>
      );

    case "checkbox":
      return (
        <div className="space-y-4">
          {questionLabel}
          <div className="space-y-3">
            {content?.options?.map((option) => (
              <Checkbox
                key={option?._id}
                isChecked={answer?.value?.includes(option?._id) || false}
                checkboxBoxClass="h-5 w-5 flex-shrink-0"
                checkboxLabel={!option?.other ? option?.name : "Other"}
                onChange={(e) => onHandleChange(content?._id, "value", e.target.checked, content?.question_type, option?._id)}
              />
            ))}
          </div>
          {answer?.value?.includes(content?.options?.find((option) => option?.other)?._id) && (
            <InputGroup
              error={isRequired}
              isDisabled={response}
              label="Specify other"
              inputValue={answer?.other_value || ""}
              onChange={(e) => onHandleChange(content?._id, "other_value", e.target.value, content?.question_type)}
            />
          )}
        </div>
      );
    case "select":
      return (
        <div className="space-y-4">
          {questionLabel}
          {response ? (
            <div className="text-gray-700">
              {content?.multiple_select ? (
                <>
                  {Array.isArray(answer?.value) && answer.value.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {answer.value
                        .filter((selectedId) => content?.options?.some((opt) => opt?._id === selectedId))
                        .map((selectedId) => {
                          const option = content?.options?.find((opt) => opt?._id === selectedId);
                          return (
                            <span
                              key={selectedId}
                              className="inline-flex select-none items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 outline-none focus:outline-none focus:ring-0">
                              {option.name}
                            </span>
                          );
                        })}
                    </div>
                  )}

                  {Array.isArray(answer?.other_value) && answer.other_value.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border p-2">
                      <span className="text-xs font-semibold text-gray-600">Other:</span>
                      {answer.other_value.map((val, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                          {val}
                        </span>
                      ))}
                    </div>
                  )}

                  {!answer?.value?.length && !answer?.other_value?.length && <p className="text-gray-700">No options selected</p>}
                </>
              ) : (
                content?.options?.find((option) => option?._id === answer?.value)?.name || <p className="text-gray-700">{"No option seleced"}</p>
              )}
            </div>
          ) : content?.multiple_select ? (
            <>
              <MultiSelectObject
                title=""
                placeholder="Search and select options..."
                defaultOptions={[
                  ...content?.options
                    ?.filter((option) => !option?.other)
                    ?.map((option, index) => ({
                      key: option?._id || index,
                      value: option?.name,
                      selected: Array.isArray(answer?.value) ? answer?.value?.includes(option?._id) : false,
                      object: option,
                    })),
                  ...customOptions.map((label, idx) => ({
                    key: label,
                    value: label,
                    selected: Array.isArray(answer?.other_value) ? answer?.other_value?.includes(label) : false,
                    object: { _id: label, name: label, custom: true },
                    image: null,
                  })),
                  ...content?.options
                    ?.filter((option) => option?.other)
                    ?.map((option, index) => ({
                      key: option?._id || `other-${index}`,
                      value: option?.name,
                      isOtherButton: true,
                      onClick: () => setShowCustomTagModal(true),
                      selected: false,
                      object: option,
                    })),
                ]}
                searchable={true}
                strictOnChangeMode={false}
                searchableFields={["name"]}
                uniqueIdentifier="_id"
                isSelectAll={true}
                emptyListText="No options available"
                showSelectedItems={true}
                showSelectedItemsMaxCount={3}
                searchComponent={(optionObject) => (
                  <div className="flex items-center gap-x-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-medium text-gray-900">{optionObject?.name}</p>
                      </div>
                      {optionObject?.description && <p className="truncate text-xs text-gray-500">{optionObject?.description}</p>}
                    </div>
                  </div>
                )}
                bubbleComponent={(optionObject) => (
                  <div className="flex max-w-[200px] items-center gap-2">
                    <span className="truncate text-sm font-medium">{optionObject?.name}</span>
                  </div>
                )}
                onChange={(updatedOptions) => {
                  const selected = updatedOptions.filter((o) => o.selected);
                  const selectedValues = selected.filter((o) => !o.object.custom).map((o) => o.object?._id || o.key);
                  onHandleChange(content?._id, "value", selectedValues, content?.question_type);
                  const selectedOtherValues = selected.filter((o) => o.object.custom).map((o) => o.object?._id || o.key);
                  onHandleChange(content?._id, "other_value", selectedOtherValues, content?.question_type);
                }}
                drowdownWindowMaxWidth="!max-w-[480px]"
                dropdownClassName="border border-gray-200 shadow-lg"
                error={isRequired && (!answer?.value || answer?.value?.length === 0)}
              />

              {showCustomTagModal && (
                <Modal
                  title="Add Custom Options"
                  isOpen={showCustomTagModal}
                  childClasses="!px-4"
                  onSuccess={commitCustomOptions}
                  defaultOptions={{
                    onCancelButtonVisible: true,
                    onSuccessButtonVisible: true,
                    onCancelButtonText: "Close",
                    onSuccessButtonText: "Save",
                  }}
                  onCancel={() => setShowCustomTagModal?.(false)}>
                  <div className="min-h-[100px] space-y-4">
                    <div>
                      <TagInput
                        existingTags={customOptions}
                        onAddTag={(tagValue) => setCustomOptions((prev) => [...prev, tagValue])}
                      />
                    </div>
                    {customOptions.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Custom Options Added:</label>
                        <div className="flex flex-wrap gap-2">
                          {customOptions.map((label, idx) => (
                            <div
                              key={`custom_${idx}`}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-sm font-medium text-highlightColor">
                              <span>{label}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setCustomOptions((prev) => prev.filter((_, i) => i !== idx));
                                }}
                                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-highlightColor hover:bg-gray-300 focus:outline-none">
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Modal>
              )}
            </>
          ) : (
            <SelectMenu
              required={isRequired}
              labelClass="mb-3 !font-semibold !text-base"
              startIndex={content?.options?.findIndex((option) => option?._id === answer?.value)}
              options={content?.options?.map((option) => ({
                key: !option?.other ? option?.name : "Other",
                value: option?._id,
              }))}
              setOption={(option) => onHandleChange(content?._id, "value", option.value, content?.question_type)}
              strategy="fixed"
            />
          )}

          {!content?.multiple_select &&
            (() => {
              const otherOption = content?.options?.find((option) => option?.other);
              const hasOtherSelected = answer?.value === otherOption?._id;

              return (
                hasOtherSelected && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <InputGroup
                      error={isRequired && !answer?.other_value?.trim()}
                      isDisabled={response}
                      label="Other"
                      placeholder="Enter your custom option..."
                      inputValue={answer?.other_value || ""}
                      onChange={(e) => onHandleChange(content?._id, "other_value", e.target.value, content?.question_type)}
                      className="bg-white"
                    />
                  </div>
                )
              );
            })()}
        </div>
      );

    case "file":
      return response ? (
        <div className="space-y-2">
          {questionLabel}
          {Array.isArray(answer?.value) && answer.value.length > 0 ? (
            <div className="flex flex-wrap gap-x-2">
              {answer.value.map((file, idx) => (
                <div
                  key={idx}
                  onClick={() => window.open(file?.url, "_blank")}
                  className="relative flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-2 py-2 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:underline">
                  <span className="text-sm font-medium text-gray-700">{file?.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-700">No file uploaded</p>
          )}
        </div>
      ) : (
        <FileUpload
          content={content}
          selectedFiles={answer?.value || []}
          onHandleChange={onHandleChange}
        />
      );

    case "radio_grid":
    case "checkbox_grid":
      return (
        <FormChoiceGrid
          content={content}
          answer={answer?.value || []}
          onHandleChange={onHandleChange}
        />
      );

    case "linear_scale":
      return (
        <div className="space-y-4">
          <RadioGroup
            required={isRequired}
            name={`option-${content?._id}`}
            labelClass="!font-semibold"
            subtitle=""
            title={content?.name}
            layout="scale"
            scaleTitle={{ from: content?.linear_scale_from_label, to: content?.linear_scale_to_label }}
            defaultChecked={answer?.value}
            options={Array.from({ length: content?.linear_scale_to - content?.linear_scale_from + 1 }, (_, index) => content?.linear_scale_from + index).map((value) => ({ id: value, title: value }))}
            onChange={(id) => onHandleChange(content?._id, "value", parseInt(id), content?.question_type)}
          />
        </div>
      );

    case "date":
      return (
        <div className="space-y-4">
          {questionLabel}
          {response ? (
            <p className="text-gray-700">
              {answer?.value
                ? new Date(answer.value).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "No date selected"}
            </p>
          ) : (
            <div className="flex h-[47px] w-full items-center justify-start rounded-md border border-gray-300 !ring-0 focus-within:border-highlightColor md:h-[40px] 2xl:h-[47px]">
              <div className="relative w-full">
                <Flatpickr
                  placeholder="MM/DD/YYYY"
                  className="h-8 w-full rounded-md !border-none px-3 !ring-0"
                  name="date"
                  value={answer?.value || ""}
                  onChange={([selectedDate]) => onHandleChange(content?._id, "value", selectedDate ? new Date(selectedDate).toISOString() : "", content?.question_type)}
                  options={{
                    altInput: true,
                    altFormat: "m/d/Y",
                    dateFormat: "d.m.y",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      );

    case "time":
      return (
        <div className="space-y-4">
          {questionLabel}
          {response ? (
            <p className="text-gray-700">
              {answer?.value
                ? new Date(answer.value).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "No time selected"}
            </p>
          ) : (
            <div className="flex h-[47px] w-full items-center justify-start rounded-md border border-gray-300 !ring-0 focus-within:border-highlightColor md:h-[40px] 2xl:h-[47px]">
              <div className="relative w-full">
                <Flatpickr
                  className="h-8 w-full rounded-md !border-none px-3 !ring-0"
                  placeholder="00:00 AM"
                  name="time"
                  value={answer?.value || ""}
                  onChange={([selectedTime]) => onHandleChange(content?._id, "value", selectedTime ? new Date(selectedTime).toISOString() : "", content?.question_type)}
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: "h:i K",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};

const TagInput = ({ onAddTag, existingTags = [], placeholder = "Add custom option...", className = "" }) => {
  const [inputValue, setInputValue] = useState("");

  const trimmed = inputValue.trim();

  const isDuplicate = existingTags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());

  const addTag = () => {
    if (!trimmed || isDuplicate) return;
    onAddTag(trimmed);
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-highlightColor focus:outline-none focus:ring-1 focus:ring-highlightColor"
        />
      </div>
      <button
        type="button"
        onClick={addTag}
        disabled={!trimmed || isDuplicate}
        className="hover:bg-highlightColor/90 inline-flex items-center rounded-md bg-highlightColor px-3 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50">
        <PlusIcon className="mr-1 h-4 w-4" />
        Add
      </button>
    </div>
  );
};

export const validateCustomInputField = (value, validation, content) => {
  const { validation_type, validation_operation, operation_values, custom_validation_message } = validation;

  const getErrorMessage = (defaultMsg) => custom_validation_message || defaultMsg;

  if (validation_type === "number") {
    if (!/^-?\d+(\.\d+)?$/.test(String(value).trim())) {
      return getErrorMessage(`${content.name} must be a valid number`);
    }

    const numValue = Number(value);
    const targetValue = operation_values?.number_value;
    const toValue = operation_values?.number_to_value;

    switch (validation_operation) {
      case "equal_to":
        if (numValue !== targetValue) {
          return getErrorMessage(`${content.name} must be equal to ${targetValue}`);
        }
        break;
      case "not_equal_to":
        if (numValue === targetValue) {
          return getErrorMessage(`${content.name} must not be equal to ${targetValue}`);
        }
        break;
      case "greater_than":
        if (numValue <= targetValue) {
          return getErrorMessage(`${content.name} must be greater than ${targetValue}`);
        }
        break;
      case "greater_than_or_equal":
        if (numValue < targetValue) {
          return getErrorMessage(`${content.name} must be greater than or equal to ${targetValue}`);
        }
        break;
      case "less_than":
        if (numValue >= targetValue) {
          return getErrorMessage(`${content.name} must be less than ${targetValue}`);
        }
        break;
      case "less_than_or_equal":
        if (numValue > targetValue) {
          return getErrorMessage(`${content.name} must be less than or equal to ${targetValue}`);
        }
        break;
      case "between":
        if (numValue < targetValue || numValue > toValue) {
          return getErrorMessage(`${content.name} must be between ${targetValue} and ${toValue}`);
        }
        break;
      case "not_between":
        if (numValue >= targetValue && numValue <= toValue) {
          return getErrorMessage(`${content.name} must not be between ${targetValue} and ${toValue}`);
        }
        break;
      default:
        break;
    }
  }

  if (validation_type === "text") {
    const textValue = String(value);
    const textPattern = operation_values?.text_value;

    switch (validation_operation) {
      case "contains":
        if (!textValue.includes(textPattern)) {
          return getErrorMessage(`${content.name} must contain "${textPattern}"`);
        }
        break;
      case "does_not_contain":
        if (textValue.includes(textPattern)) {
          return getErrorMessage(`${content.name} must not contain "${textPattern}"`);
        }
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(textValue)) {
          return getErrorMessage(`${content.name} must be a valid email address`);
        }
        break;
      case "url":
        try {
          new URL(textValue);
        } catch (e) {
          return getErrorMessage(`${content.name} must be a valid URL`);
        }
        break;
      default:
        break;
    }
  }

  return null;
};
